export const medSpasData = {
  industry: "Med Spas",
  slug: "med-spas",
  
  seo: {
    title: "Intake Forms for Med Spas | HeyIntake",
    description: "Streamline patient intake for your medical spa with HIPAA-friendly forms. Capture medical history, treatment goals, and consent before appointments.",
    keywords: [
      "med spa intake form",
      "medical spa patient questionnaire",
      "aesthetic clinic intake",
      "botox consultation form",
      "medspa client onboarding",
      "cosmetic procedure intake"
    ]
  },

  heroTitle: "Intake Forms for Med Spas",
  heroSubtitle: "Capture medical history, treatment goals, and consent before patients arrive. Reduce wait times, increase consultations.",
  heroDescription: "Your patients expect a premium experience from the moment they inquire. HeyIntake helps you collect complete medical history, aesthetic goals, and treatment preferences so consultations are productive, not administrative.",
  
  whyAIScoring: {
    title: "Why Med Spas Need Professional Digital Intake",
    intro: "In the medical aesthetics industry, first impressions matter. Clipboards and paper forms don't match the premium experience your patients expect.",
    paragraphs: [
      "Med spa patients are investing in themselves — they expect a seamless, sophisticated experience. Making them fill out paper forms in your lobby while other clients watch isn't just inefficient, it undermines your premium positioning.",
      "HeyIntake provides the digital-first experience discerning patients expect. They complete intake on their phone before arriving, including medical history, current medications, allergies, and treatment goals. Your staff can review everything in advance and prepare for a productive consultation.",
      "For med spas offering Botox, fillers, laser treatments, and body contouring, this means shorter wait times, more consultation time, better treatment matching, and the polished experience that justifies premium pricing."
    ]
  },

  painPoints: [
    {
      title: "Long Lobby Wait Times",
      description: "New patients spend 15-20 minutes filling out clipboards while your schedule backs up."
    },
    {
      title: "Incomplete Medical History",
      description: "Patients forget to mention medications, allergies, or conditions that affect treatment safety."
    },
    {
      title: "Unqualified Consultations",
      description: "Booking consultations for treatments the patient isn't a good candidate for."
    },
    {
      title: "Paper Form Chaos",
      description: "Handwritten forms get lost, can't be searched, and don't integrate with your systems."
    },
    {
      title: "Consent Documentation Gaps",
      description: "Missing or incomplete consent forms create liability exposure."
    },
    {
      title: "No-Show New Patients",
      description: "Leads who book consultations but never show up, wasting prime appointment slots."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Demographics",
      fields: [
        "Full legal name",
        "Date of birth",
        "Email and phone",
        "Emergency contact",
        "How did you hear about us?"
      ]
    },
    {
      category: "Medical History",
      fields: [
        "Current medications",
        "Known allergies (especially lidocaine, latex)",
        "Previous cosmetic procedures",
        "Medical conditions (autoimmune, bleeding disorders)",
        "Pregnancy/nursing status"
      ]
    },
    {
      category: "Treatment Interest",
      fields: [
        "Areas of concern",
        "Treatments you're interested in",
        "Previous experience with injectables/lasers",
        "Desired outcomes",
        "Timeline expectations"
      ]
    },
    {
      category: "Consultation Preferences",
      fields: [
        "Preferred appointment times",
        "Budget range",
        "Financing interest",
        "Photo consent for records",
        "Marketing opt-in"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Med Spa Intake Form",
    description: "Here's what a professional aesthetic intake looks like:",
    fields: [
      { label: "What treatment are you interested in?", type: "select", options: ["Botox/Dysport", "Dermal Fillers", "Laser Treatment", "Body Contouring", "Skincare/Facials", "Multiple/Unsure"] },
      { label: "Have you had this treatment before?", type: "select", options: ["Yes", "No", "Similar treatment"] },
      { label: "Primary area of concern", type: "text" },
      { label: "Any allergies we should know about?", type: "textarea" },
      { label: "Current medications", type: "textarea" },
      { label: "Are you pregnant or nursing?", type: "select", options: ["No", "Yes - Pregnant", "Yes - Nursing"] },
      { label: "Budget for treatment", type: "select", options: ["$500-$1,000", "$1,000-$2,500", "$2,500-$5,000", "$5,000+"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Med Spas",
    subtitle: "From inquiry to consultation in three steps",
    steps: [
      {
        title: "Patient Completes Intake",
        description: "New patients fill out your branded form with medical history and treatment goals from their phone."
      },
      {
        title: "Staff Reviews in Advance",
        description: "Your team reviews submissions before appointments, flagging contraindications or preparing recommendations."
      },
      {
        title: "Consultations Convert",
        description: "Spend consultation time discussing treatments, not collecting basic information. Close more packages."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Patient Qualification for Med Spas",
    description: "HeyIntake's AI helps you identify ideal patients and flag potential contraindications before consultation.",
    factors: [
      "Treatment candidacy based on medical history",
      "Budget alignment with your services",
      "Realistic expectation indicators",
      "Previous treatment experience",
      "Urgency and booking intent"
    ],
    exampleLead: {
      name: "Amanda Richards",
      context: "Botox Interest • No Contraindications • $2K Budget",
      summary: "First-time Botox patient, no allergies or contraindications. Interested in forehead and crow's feet. Budget aligns with full treatment. Ready to book this week.",
      score: 93
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Screen for Contraindications",
      description: "AI flags patients with conditions or medications that affect treatment eligibility."
    },
    {
      icon: "file",
      title: "Digital Consent Forms",
      description: "Collect treatment consent and photo release before appointments."
    },
    {
      icon: "clock",
      title: "Eliminate Lobby Wait",
      description: "Patients arrive ready for consultation, not paperwork."
    },
    {
      icon: "users",
      title: "Match Treatment to Patient",
      description: "Route inquiries to the right provider based on treatment interest."
    },
    {
      icon: "zap",
      title: "Automate Confirmations",
      description: "Send appointment confirmations, reminders, and pre-care instructions automatically."
    },
    {
      icon: "chart",
      title: "Track Consultation Pipeline",
      description: "See who's inquired, consulted, and converted to treatment."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Dentists", path: "/intake-form-for-dentists" },
    { name: "Intake Forms for Therapists", path: "/intake-form-for-therapists" },
    { name: "Intake Forms for Fitness Trainers", path: "/intake-form-for-fitness-trainers" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" }
  ],

  ctaText: "Ready to Elevate Your Patient Experience?"
};
